import { useCallback, useState } from "react";
import { MarketRate, OrderType, TradeForm } from "@cede/types";
import { API_ENDPOINT, estimateExecutedPriceFromOrderBook, Precise, unformatNumberAsString } from "@cede/utils";
import { useApi } from "@/hooks/core/useApi";
import { useDebounceFn } from "@/hooks/core/useDebounceFn";
import { getLastOrderTransaction } from "@/hooks/trading/multi-transaction/useMultipleTransactions";
import {
  MultipleTransactionsPreparedOrder,
  MultipleTransactionsPreparedWithdrawal,
} from "@/hooks/trading/multi-transaction/useMultipleTransactionsStore";
import { useHubUser } from "@/hooks/user/useHubUser";
import { useOrderBookStore } from "@/store/orderBookStore";
import { TradeFormAction } from "@/types";

type UseTradeAmountComputation = {
  tradeForm: TradeForm;
  dispatchForm: (value: TradeFormAction<TradeForm>) => void;
  transactions: (MultipleTransactionsPreparedOrder | MultipleTransactionsPreparedWithdrawal)[];
  shouldUseTradeSquare: boolean;
  marketRate: MarketRate;
  orderType: OrderType;
};

export const useTradeAmountComputation = ({
  tradeForm,
  dispatchForm,
  transactions,
  shouldUseTradeSquare,
  marketRate,
  orderType,
}: UseTradeAmountComputation) => {
  const apiHelper = useApi();
  const { auth, hubUser } = useHubUser();
  const orderBook = useOrderBookStore((state) => state.orderBook);
  const [amountFromIsLoading, setAmountFromIsLoading] = useState(false);
  const [amountToIsLoading, setAmountToIsLoading] = useState(false);

  const getRate = () => {
    if (orderType === OrderType.MARKET) {
      return tradeForm.orderSide === "buy" ? marketRate.ask : marketRate.bid;
    } else {
      return unformatNumberAsString(tradeForm.limitPrice).toString();
    }
  };

  const computeEstimatedTradePrice = async (valueToCompute: "amountFrom" | "amountTo") => {
    try {
      if (orderType === OrderType.MARKET) {
        const amount = unformatNumberAsString(
          (valueToCompute === "amountTo" ? tradeForm.amountFrom : tradeForm.amountTo) || "0",
        );
        const isQuote =
          (tradeForm.orderSide === "sell" && valueToCompute === "amountFrom") ||
          (tradeForm.orderSide === "buy" && valueToCompute === "amountTo");

        const account = hubUser?.exchangeAccounts.find((acc) => acc.id === Number(tradeForm.selectedAccount?.value));
        if (!account) return null;

        // Check if we can compute the estimated executed price from the order book store
        if (
          orderBook &&
          orderBook.pairSymbol === tradeForm.market?.pairSymbol &&
          orderBook.exchangeId === account.exchangeId
        ) {
          const orderbookSide = tradeForm.orderSide === "buy" ? orderBook.asks : orderBook.bids;
          const estimatedPrice = estimateExecutedPriceFromOrderBook(amount, orderbookSide, isQuote);
          return estimatedPrice.toString();
        }

        // Otherwise, fallback to the server side
        if (
          !auth.identityToken ||
          !tradeForm.selectedAccount?.value ||
          !tradeForm.market?.pairSymbol ||
          !tradeForm.orderSide
        )
          return null;

        const res = await apiHelper.getWithAuth(auth, API_ENDPOINT.HUB_TRADE_ESTIMATE_PRICE_FROM_ORDER_BOOK, {
          queryParams: {
            accountId: tradeForm.selectedAccount.value,
            exchangeId: account.exchangeId,
            pairSymbol: tradeForm.market.pairSymbol,
            amountIn: amount.toString(),
            isQuote: isQuote.toString(),
            orderSide: tradeForm.orderSide,
          },
        });
        return res?.estimatedPrice.toString();
      } else {
        return unformatNumberAsString(tradeForm.limitPrice).toString();
      }
    } catch {
      return null;
    }
  };

  const computeAmountTo = useCallback(async () => {
    setAmountToIsLoading(true);
    if (!tradeForm.tokenTo || !tradeForm.tokenFrom || tradeForm.amountFrom === "0") {
      dispatchForm({
        field: "amountTo",
        value: "0",
      });
      setAmountToIsLoading(false);
      return;
    }

    if (transactions && transactions.length && shouldUseTradeSquare) {
      dispatchForm({
        field: "amountTo",
        value: getLastOrderTransaction(transactions)?.to?.amount || "0",
      });
      setAmountToIsLoading(false);
      return;
    }

    const rate = (await computeEstimatedTradePrice("amountTo")) ?? getRate();

    if (!tradeForm.market || !tradeForm.tokenFrom || !tradeForm.tokenTo || !tradeForm.amountFrom) {
      setAmountToIsLoading(false);
      return;
    }

    if (rate === "0") {
      dispatchForm({
        field: "amountTo",
        value: "0",
      });
      setAmountToIsLoading(false);
      return;
    }

    const amountFrom = String(unformatNumberAsString(tradeForm.amountFrom));
    const estimatedAmount =
      tradeForm.orderSide === "sell"
        ? Precise.stringMul(amountFrom, rate) || "0"
        : Precise.stringDiv(amountFrom, rate) || "0";
    dispatchForm({
      field: "amountTo",
      value: estimatedAmount.toString(),
    });
    setAmountToIsLoading(false);
  }, [
    tradeForm.market,
    tradeForm.tokenFrom,
    tradeForm.tokenTo,
    tradeForm.amountFrom,
    tradeForm.orderSide,
    tradeForm.selectedAccount,
    transactions,
    shouldUseTradeSquare,
  ]);

  const computeAmountFrom = useCallback(async () => {
    if (!tradeForm.market || !tradeForm.tokenFrom || !tradeForm.tokenTo || !tradeForm.amountTo) return;
    setAmountFromIsLoading(true);
    const rate = (await computeEstimatedTradePrice("amountFrom")) ?? getRate();

    if (rate === "0") {
      dispatchForm({
        field: "amountFrom",
        value: "0",
      });
      setAmountFromIsLoading(false);
      return;
    }

    const amountTo = String(unformatNumberAsString(tradeForm.amountTo));
    const estimatedAmount =
      tradeForm.orderSide === "sell"
        ? Precise.stringDiv(amountTo, rate) || "0"
        : Precise.stringMul(amountTo, rate) || "0";

    dispatchForm({
      field: "amountFrom",
      value: estimatedAmount?.toString(),
    });
    setAmountFromIsLoading(false);
  }, [
    tradeForm.market,
    tradeForm.tokenFrom,
    tradeForm.tokenTo,
    tradeForm.amountTo,
    tradeForm.orderSide,
    tradeForm.selectedAccount,
    transactions,
    shouldUseTradeSquare,
  ]);

  const { debouncedFn: debouncedComputeAmountTo, isDebouncing: isDebouncingComputeAmountTo } = useDebounceFn(
    computeAmountTo,
    500,
  );
  const { debouncedFn: debouncedComputeAmountFrom, isDebouncing: isDebouncingComputeAmountFrom } = useDebounceFn(
    computeAmountFrom,
    500,
  );

  return {
    computeAmountTo: debouncedComputeAmountTo,
    computeAmountFrom: debouncedComputeAmountFrom,
    computeEstimatedTradePrice,
    amountFromIsLoading: amountFromIsLoading || isDebouncingComputeAmountFrom,
    amountToIsLoading: amountToIsLoading || isDebouncingComputeAmountTo,
  };
};
